



























































































































































import {
	computed,
	defineComponent,
	Ref,
	ref,
	watch,
	watchEffect,
} from '@vue/composition-api';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { Routes } from '@front-app/constants/routes';
import { ButtonPalettes } from '@front-app/components/buttons/palettes';
import EventCard from '@front-app/components/cards/EventCard.vue';
import { Size } from '@front-app/components/constants';
import { useGroupsCustomHighlights } from '@front-app/store/modules/highlights';
import { useGroupEvents } from '@front-app/hooks/Group/useGroupEvents';
import { trackGroupSimpleAction } from '@front-app/services/ActionApi/ActionApi';
import { BannerLocation, GroupAuditAction } from '@front-app/services/ActionApi/ActionModel';
import { ContentOrdering, ContentPageParamsV2, ContentSort } from '@front-app/services/GroupsApi/GroupsContentApi';
import {
	ContentType,
	ContentV2,
	DiscussionContentItem,
	ReactionType,
} from '@front-app/services/GroupsApi/DiscussionModel';
import { useAsyncStatus, useIsLoading } from '@front-app/hooks/useAsyncStatus';
import { getGroupContentStore } from '@front-app/store/modules/groupContent';
import { GroupsStore } from '@front-app/store/singletons/Groups';
import { CurrentUserStore } from '@front-app/store/singletons/User';
import {
	canViewContentTab,
	canViewGroupEvents,
	canViewInviteCampaignTab,
	useCanSeeCategoryWidget,
	useCanSeeMemberWidget,
	canViewPinnedHighlights,
} from '@front-app/util/permission';
import { EVENTS_ICON } from '@front-app/vendor/icon-definitions';
import { useDiscussionFollow } from '@front-app/components/discussion/useDiscussionFollow';
import { useContentRouting } from '@front-app/modules/content/hooks/useContentRouting';
import { useGroupContentStore } from '@front-app/store/piniaModules/groupContent';
import Highlights from '@front-app/modules/group/highlights/Highlights.vue';
import PaddedContent from '@front-app/components/layouts/columns/PaddedContent.vue';
import LoaderState from '@front-app/components/LoaderState.vue';
import MemberList from '@front-app/components/group/MemberList.vue';
import CategoriesList from '@front-app/components/group/CategoriesList.vue';
import MixedContentCard from '@front-app/components/cards/content/MixedContentCard.vue';
import { ACTIVE_MEMBER_ROLES, GroupMember } from '@front-app/services/UserApi/UserModel';
import { getGroupMemberPage } from '@front-app/services/GroupsApi/GroupsMemberApi';
import { MemberSort } from '@front-app/modules/groupMember/hooks/useSortMemberRadio';
import CtaButton from '@front-app/components/buttons/CtaButton.vue';
import { isLaterTodayOrFutureEvent, isNotPastEvent, orderEventsOldToNew } from '../events/filters';
import EventGridList from '../events/GridList.vue';

const NUM_HIGHLIGHT_CONTENTS = 5;
const NUM_HIGHLIGHT_EVENTS = 3;

const CONTENT_INFO_PANEL_ARIA_LABEL_BY_TYPE = {
	[ContentType.FILE]: 'group.content.aria.view-file-details',
	[ContentType.LINK]: 'group.content.aria.view-link-details',
	[ContentType.DISCUSSION]: 'group.content.aria.go-to-discussion',
};

export default defineComponent({
	components: {
		CtaButton,
		EventCard,
		FontAwesomeIcon,
		Highlights,
		PaddedContent,
		LoaderState,
		MemberList,
		CategoriesList,
		EventGridList,
		MixedContentCard,
	},
	props: {
		groupId: {
			type: String,
			required: true,
		},
	},
	setup(props, ctx) {
		const group = computed(() => GroupsStore.getInstance().groupById(props.groupId));
		watchEffect(() => {
			if (!props.groupId) {
				return;
			}
			trackGroupSimpleAction(props.groupId, GroupAuditAction.HOME_VIEW);
		});

		// Highlights
		const canViewHighlights = computed(() => canViewPinnedHighlights(group.value));

		const {
			highlights,
			status: highlightsFetchStatus,
		} = useGroupsCustomHighlights(computed(() => props.groupId));
		const isLoadingHighlights = useIsLoading(highlightsFetchStatus);
		// END Highlights

		// Members
		const canSeeMembers = useCanSeeMemberWidget(group);
		const {
			track,
			isLoading: isLoadingMembers,
		} = useAsyncStatus();
		const activeMembers: Ref<GroupMember[]> = ref([]);
		const memberFetcher = async () => {
			activeMembers.value = await track(getGroupMemberPage(props.groupId, ACTIVE_MEMBER_ROLES, 1, MemberSort.LAST_JOIN));
		};
		watch(() => props.groupId, (curr, prev) => {
			if (canSeeMembers.value && curr && curr !== prev) {
				memberFetcher();
			}
		}, { immediate: true });
		// END Members

		// Categories
		const canSeeCategories = useCanSeeCategoryWidget(group);
		// END Categories

		// Group Activity
		const canViewGroupActivity = computed(() => canViewContentTab(group.value));
		const groupActivityParams: Ref<ContentPageParamsV2> = ref({
			type: [ContentType.FILE, ContentType.DISCUSSION, ContentType.LINK],
			order: ContentOrdering.DESCENDING,
			sort: ContentSort.DATE,
			pageSize: NUM_HIGHLIGHT_CONTENTS,
			search: '',
		});
		const {
			contents: groupActivity,
			update,
		} = getGroupContentStore(computed(() => props.groupId), groupActivityParams);
		const toggleDiscussionFollow = (content: DiscussionContentItem) => {
			useDiscussionFollow(ref(content)).toggleLocalFollowState();
		};
		const routeToContent = (content: ContentV2) => {
			if (content.type === ContentType.DISCUSSION) {
				ctx.root.$router.push({ name: Routes.GROUP_DISCUSSION, params: { discussionId: content.id } });
			} else {
				ctx.root.$router.push({ name: Routes.GROUP_CONTENT, query: { showContent: content.id } });
			}
		};
		const {
			displayInNewTab: displayContentInNewTab,
		} = useContentRouting(ctx.root.$router);
		const toAllContent = computed(() => ({
			name: Routes.GROUP_CONTENT,
			params: { groupId: props.groupId },
		}));
		// END Group Activity

		// Upcoming Events
		const canViewEvents = computed(() => canViewGroupEvents(group.value));
		const { events } = useGroupEvents(computed(() => props.groupId));
		const highlightEvents = computed(() => events.value
			.filter(isNotPastEvent)
			.sort(orderEventsOldToNew)
			.slice(0, NUM_HIGHLIGHT_EVENTS));
		const nextEvent = computed(() => events.value.filter(isLaterTodayOrFutureEvent).sort(orderEventsOldToNew)[0]);
		const toAllEvents = computed(() => ({
			name: Routes.GROUP_EVENTS,
			params: { groupId: props.groupId },
		}));
		// END Upcoming Events

		// Invites
		const { isCsr } = CurrentUserStore;
		const canSeeInviteLink = computed(() => canViewInviteCampaignTab(group.value, isCsr.value));
		const toInviteTab = computed(() => ({
			name: Routes.GROUP_ADMIN_ADD_MEMBERS,
			params: { groupId: props.groupId },
		}));
		// END Invites

		// Reactions
		const groupContentStore = useGroupContentStore();
		const reactToContent = async (contentType: ContentType, contentId: string, reactionType: ReactionType) => {
			await groupContentStore.react(props.groupId, contentType, contentId, reactionType);

			// Update content in the old content store
			const updatedContentInNewStore = groupContentStore.getById(contentId);
			if (updatedContentInNewStore) {
				update([updatedContentInNewStore]);
			}
		};
		// END Reactions

		return {
			group,
			GROUP_CONTENT: Routes.GROUP_CONTENT,
			// Highlights
			canViewHighlights,
			highlights,
			isLoadingHighlights,
			// Members
			activeMembers,
			isLoadingMembers,
			canSeeMembers,
			// Categories
			canSeeCategories,
			// Group Activity
			canViewGroupActivity,
			toAllContent,
			groupActivity,
			toggleDiscussionFollow,
			routeToContent,
			displayContentInNewTab,
			CONTENT_INFO_PANEL_ARIA_LABEL_BY_TYPE,
			// Invites
			canSeeInviteLink,
			toInviteTab,
			// Events
			EVENTS_ICON,
			canViewEvents,
			highlightEvents,
			nextEvent,
			toAllEvents,
			BannerLocation,
			// Reactions
			reactToContent,
			// Util
			Size,
			ButtonPalettes,
		};
	},
});
